import { useState } from 'react'
import { Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import type { OverrideGovernance } from '@/types/bundle'
import { describeGovernancePath } from '../governancePathTemplates'
import { AddGovernancePathModal } from './AddGovernancePathModal'

export interface GovernanceTabProps {
  governance: OverrideGovernance
  onChange: (governance: OverrideGovernance) => void
}

interface AllowlistRow {
  path: string
}

// Plain-English explanation for each real, currently-shipped denylist
// prefix (confirmed against every bundle fixture in legalflow-idp's
// configs/bundles/*/global_defaults.json - all 11 entries below are the
// complete real-world set today). Falls back to no label (just the raw
// mono path) for anything a firm adds later that isn't one of these -
// same "show it, don't hide it" treatment as the allowlist's own
// describeGovernancePath.
const DENYLIST_PREFIX_DESCRIPTIONS: Record<string, string> = {
  'global_defaults.hard_caps': 'Platform-wide document/page/size ceilings - no firm override can touch these at all.',
  'global_defaults.override_governance': "This governance config itself - a firm can never loosen its own restrictions.",
  'global_defaults.ocr_fallback': 'The platform-wide OCR fallback engine and its settings.',
  'global_defaults.output_options': 'Platform-wide output settings, e.g. whether documents can be echoed back at all.',
  'product_types.*.mode': 'Which processing mode any product type runs in.',
  'product_types.*.orchestration': "Any product type's extraction pipeline/orchestration setup.",
  'product_types.*.document_requirements': 'Which document types any product type requires.',
  'product_types.*.echo_original_documents': "Any product type's own echo-original-documents setting.",
  extractors: 'Every extractor definition - models, prompts, timeouts, everything.',
  'document_types.*.identification': 'How any document type is identified and matched.',
  'document_types.*.echo_original_documents': "Any document type's own echo-original-documents setting.",
}

function describeDenylistPrefix(prefix: string): string | undefined {
  return DENYLIST_PREFIX_DESCRIPTIONS[prefix]
}

// Governs what a firm's own client-override JSON is allowed to change
// (app/core/config/override.py) - denylist_prefixes/allowlist_paths/
// numeric_bounds/tightening_only are glob-path patterns matched with Python
// fnmatch semantics (see features/client-overrides/overrideUtils.ts's
// matchesGlobPath), not path-segment-aware globbing.
//
// allowlist_paths/numeric_bounds/tightening_only are three separate raw-
// string lists that all describe the *same* underlying paths - previously
// rendered as three separate wrapped-chip lists, which read as noise (the
// same long dotted path repeated three times over). Merged here into one
// table, one row per path, same "table instead of wrapped chips" fix
// Client Overrides' own OverridesSection already uses for this exact kind
// of data.
export function GovernanceTab({ governance, onChange }: GovernanceTabProps) {
  const [addModalOpen, setAddModalOpen] = useState(false)

  const allowlistPaths = governance.allowlist_paths
  const tighteningOnly = governance.tightening_only ?? []
  const numericBounds = governance.numeric_bounds

  // Union of all three, in case a live bundle ever has a numeric_bounds/
  // tightening_only entry that drifted out of sync with allowlist_paths -
  // shown rather than silently dropped, so nothing disappears from view.
  const allPaths = Array.from(new Set([...allowlistPaths, ...Object.keys(numericBounds), ...tighteningOnly]))
  const rows: AllowlistRow[] = allPaths.map((path) => ({ path }))

  function addPath(path: string) {
    if (allowlistPaths.includes(path)) return
    onChange({ ...governance, allowlist_paths: [...allowlistPaths, path] })
  }

  function removePath(path: string) {
    const nextBounds = { ...numericBounds }
    delete nextBounds[path]
    onChange({
      ...governance,
      allowlist_paths: allowlistPaths.filter((p) => p !== path),
      numeric_bounds: nextBounds,
      tightening_only: tighteningOnly.filter((p) => p !== path),
    })
  }

  function updateBound(path: string, patch: { min?: number; max?: number }) {
    onChange({ ...governance, numeric_bounds: { ...numericBounds, [path]: { ...numericBounds[path], ...patch } } })
  }

  function toggleTightening(path: string, checked: boolean) {
    onChange({
      ...governance,
      tightening_only: checked ? [...tighteningOnly, path] : tighteningOnly.filter((p) => p !== path),
    })
  }

  const columns: DataTableColumn<AllowlistRow>[] = [
    {
      key: 'path',
      header: 'Path',
      render: (row) => {
        const label = describeGovernancePath(row.path)
        return (
          <div className="flex flex-col gap-0.5">
            {label && <span className="text-body text-text-primary">{label}</span>}
            <span className="font-mono text-meta text-text-secondary">{row.path}</span>
          </div>
        )
      },
    },
    {
      key: 'min',
      header: (
        <span className="flex items-center gap-1">
          Min
          <InfoTooltip text="The smallest value a firm's own override is allowed to set for this path. Blank means no lower bound - a firm can set it as low as they like." />
        </span>
      ),
      render: (row) => (
        <input
          type="number"
          placeholder="none"
          value={numberField(numericBounds[row.path]?.min)}
          onChange={(event) => updateBound(row.path, { min: parseOptionalNumber(event.target.value) })}
          className="w-24 rounded-control border border-border bg-bg px-2 py-1 text-meta text-text-primary outline-none placeholder:text-text-disabled"
        />
      ),
    },
    {
      key: 'max',
      header: (
        <span className="flex items-center gap-1">
          Max
          <InfoTooltip text="The largest value a firm's own override is allowed to set for this path. Blank means no upper bound - a firm can set it as high as they like." />
        </span>
      ),
      render: (row) => (
        <input
          type="number"
          placeholder="none"
          value={numberField(numericBounds[row.path]?.max)}
          onChange={(event) => updateBound(row.path, { max: parseOptionalNumber(event.target.value) })}
          className="w-24 rounded-control border border-border bg-bg px-2 py-1 text-meta text-text-primary outline-none placeholder:text-text-disabled"
        />
      ),
    },
    {
      key: 'tightening',
      header: (
        <span className="flex items-center gap-1">
          Tightening only
          <InfoTooltip text="Checked: a firm may only move this value in the stricter direction (e.g. lower a page limit) - never loosen it past the platform default. Unchecked: a firm may set it anywhere within Min/Max, in either direction." />
        </span>
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={tighteningOnly.includes(row.path)}
          onChange={(event) => toggleTightening(row.path, event.target.checked)}
        />
      ),
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      render: (row) => (
        <button
          type="button"
          aria-label={`Remove ${row.path}`}
          onClick={() => removePath(row.path)}
          className="text-text-secondary hover:text-danger"
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Mode
          <InfoTooltip text="Fixed by the platform - allowlist is currently the only supported governance mode: nothing is overridable by default, and a firm's own override file can only touch a path if it's explicitly listed in Allowlisted paths below." />
        </h2>
        <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
          {governance.mode}
        </span>
      </section>

      <DenylistPrefixesSection governance={governance} onChange={onChange} />

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
            Allowlisted paths
            <InfoTooltip text="The complete list of paths a firm's own override file is permitted to touch - anything not listed here is rejected, even if it isn't denylisted. Each row can also set a Min/Max bound and a Tightening-only rule (see the column headers for what those mean)." />
          </h2>
          <button
            type="button"
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-1 rounded-control border border-border px-3 py-1.5 text-meta font-medium text-text-primary hover:border-accent/60"
          >
            <Plus size={14} strokeWidth={1.5} />
            Add path
          </button>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            headline="No paths allowlisted yet"
            description="Add a path to let a firm's client override touch it, optionally bounded by a min/max."
            action={{ label: '+ Add path', onClick: () => setAddModalOpen(true) }}
          />
        ) : (
          <DataTable columns={columns} data={rows} getRowKey={(row) => row.path} />
        )}

        <AddGovernancePathModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          existingPaths={allPaths}
          onSelect={addPath}
        />
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Path semantics
          <InfoTooltip text="Fixed by the platform, applies to every allowlisted path above. Operation 'replace_only' means an override can only replace the value already sitting at that path - it can never add a brand-new field or array entry. No implicit creation means the path must already exist in the base config before it can be overridden at all." />
        </h2>
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-meta font-medium text-text-secondary">Operation</span>
            <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
              {governance.path_semantics?.operation ?? 'replace_only'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-meta font-medium text-text-secondary">No implicit creation</span>
            <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
              {String(governance.path_semantics?.no_implicit_creation ?? true)}
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}

interface DenylistRow {
  prefix: string
}

// A defence-in-depth blocklist, conceptually separate from the allowlist
// table above (it names whole subtrees to always reject, checked before the
// allowlist) - same "one row per path, not a wrapped chip" treatment, but
// its own small inline add (no picker modal: unlike the allowlist, there's
// no small known-template set to pick from, this is inherently open-ended).
function DenylistPrefixesSection({ governance, onChange }: GovernanceTabProps) {
  const [newPrefix, setNewPrefix] = useState('')
  const rows: DenylistRow[] = governance.denylist_prefixes.map((prefix) => ({ prefix }))

  function addPrefix() {
    const trimmed = newPrefix.trim()
    if (trimmed.length === 0 || governance.denylist_prefixes.includes(trimmed)) return
    onChange({ ...governance, denylist_prefixes: [...governance.denylist_prefixes, trimmed] })
    setNewPrefix('')
  }

  function removePrefix(prefix: string) {
    onChange({ ...governance, denylist_prefixes: governance.denylist_prefixes.filter((p) => p !== prefix) })
  }

  const columns: DataTableColumn<DenylistRow>[] = [
    {
      key: 'prefix',
      header: 'Prefix',
      render: (row) => {
        const description = describeDenylistPrefix(row.prefix)
        return (
          <div className="flex flex-col gap-0.5">
            {description && <span className="text-body text-text-primary">{description}</span>}
            <span className="font-mono text-meta text-text-secondary">{row.prefix}</span>
          </div>
        )
      },
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      render: (row) => (
        <button
          type="button"
          aria-label={`Remove ${row.prefix}`}
          onClick={() => removePrefix(row.prefix)}
          className="text-text-secondary hover:text-danger"
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      ),
    },
  ]

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Denylist prefixes
        <InfoTooltip text="Whole config sections a firm's override file can never touch, no matter what - checked before the allowlist below and always wins, even if something inside one of these were also allowlisted. This is a backstop, not the main gate: the allowlist below is what actually decides what's editable." />
      </h2>

      {rows.length > 0 && <DataTable columns={columns} data={rows} getRowKey={(row) => row.prefix} />}

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newPrefix}
          onChange={(event) => setNewPrefix(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addPrefix()
            }
          }}
          placeholder="e.g. extractors"
          className="flex-1 rounded-control border border-border bg-bg px-3 py-2 font-mono text-meta text-text-primary outline-none placeholder:text-text-disabled"
        />
        <button
          type="button"
          onClick={addPrefix}
          disabled={newPrefix.trim().length === 0}
          className="flex items-center gap-1 rounded-control border border-border px-3 py-2 text-meta text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add prefix
        </button>
      </div>
    </section>
  )
}
